var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _FsChoiceButtonGroup_instances, _FsChoiceButtonGroup_internals, _FsChoiceButtonGroup_slotChangeController, _FsChoiceButtonGroup_onSlotChange;
import { fsChoiceButtonGroupDefaults, groupCanonicalName } from '@cof/fs-choice-button-core';
import choiceButtonStyles from '@cof/fs-choice-button-core/lib/choice-button.lit.js';
import fieldLabelStyles from '@cof/fs-field-label-core/lib/field-label.lit.js';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.js';
import { FsHelperText } from '@cof/fs-helper-text-wc/src/HelperText.js';
import { privateFsDefineComponent } from '@cof/safe-custom-element-define';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
privateFsDefineComponent.define(FsHelperText);
/**
 *## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-choice-button-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-choice-button-group` call
 *
 * ```javascript
 * import '@cof/fs-choice-button-wc/fs-choice-button-group.js';
 * ```
 *
 * To import the `ChoiceButtonGroup` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { ChoiceButtonGroup } from '@cof/fs-choice-button-wc';
 * ```
 * @element fs-choice-button-group
 * @slot - The child choice buttons
 * @cssprop --fs-choice-buttons-per-row - When present will determine the number of choice buttons in a given row
 */
export class FsChoiceButtonGroup extends LitElement {
    constructor() {
        super(...arguments);
        _FsChoiceButtonGroup_instances.add(this);
        /** Used for accessibility purposes */
        _FsChoiceButtonGroup_internals.set(this, this.attachInternals());
        /**
         * When set to true, the choice button group will flow into a single
         * full-width column. This property overrides any others that might be set
         * on the group, including items-per-row
         *
         * @attribute compact
         */
        this.compact = fsChoiceButtonGroupDefaults.compact;
        /**
         * Controls the layout direction of children within the group
         *
         * @attribute direction
         */
        this.direction = fsChoiceButtonGroupDefaults.direction;
        /**
         * Updates the element's overall disabled state. Toggling this attribute
         * will trigger the same effect on any child controls as this element has
         * the same semantics as a fieldset.
         *
         * @attribute disabled
         */
        this.disabled = false;
        /**
         * Used to manually set error messages. Note: this property is presentational
         * only and should not be relied upon to toggle child element validity
         *
         * @attribute error-message
         */
        this.errorMessage = '';
        /**
         * The group's collective label. This works in conjunction in place of the element's
         * `choice-button-field-label` slot. It the slot is not present, this text will render;
         * otherwise, the slot content will render even if this property or attribute is set.
         *
         * @attribute field-label
         */
        this.fieldLabel = '';
        /**
         * When set this text will be used to describe the controls in the group
         *
         * @attribute helper-text
         */
        this.helperText = '';
        /**
         * When the choice button group is not set to a column layout, this property
         * determines the number of items present in each row of the choice button group
         *
         * @attribute items-per-row
         */
        this.itemsPerRow = fsChoiceButtonGroupDefaults.itemsPerRow;
        this.validationMessage = '';
        /**
         * Used for cleaning up event listeners whenever a slotchange event fires.
         * For more information: https://developer.mozilla.org/en-US/docs/Web/API/AbortController
         * @private
         */
        _FsChoiceButtonGroup_slotChangeController.set(this, void 0);
    }
    render() {
        const groupClass = {
            'fs-choice-button-group': true,
            'fs-choice-button-group--items-per-row': !!this.itemsPerRow,
            'fs-choice-button-group--column': this.direction === 'column'
        };
        return html `<fieldset
      class="fs-fieldset"
      ?disabled="${this.disabled}"
    >
      <div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
        <legend class="fs-fieldset__legend fs-field-label ${this.disabled ? 'fs-field-label--disabled' : ''}">
          <slot name="choice-button-field-label">
            ${this.fieldLabel}
          </slot>
        </legend>

        <div
          aria-describedby="helper-text"
          id="group"
          class="${classMap(groupClass)}"
          style="--fs-choice-buttons-per-row: ${this.itemsPerRow}"
        >
          <slot
            id="default"
            @slotchange="${__classPrivateFieldGet(this, _FsChoiceButtonGroup_instances, "m", _FsChoiceButtonGroup_onSlotChange)}"
          ></slot>
        </div>

        <private-fs-helper-text
          error="${this.errorMessage || this.validationMessage}"
          helper="${this.helperText}"
          id="helper-text"
          type="${this.errorMessage ? 'error' : this.disabled ? 'disabled' : 'default'}"
        ></private-fs-helper-text>
      </div>
    </fieldset>`;
    }
    firstUpdated() {
        __classPrivateFieldGet(this, _FsChoiceButtonGroup_internals, "f").role = 'group';
    }
    /**
     * Update the child controls' compact and disabled states if it is modified on the fieldset/group
     * element. This is a destructive action and won't remember previous states of the
     * child components.
     * @private
     */
    updated(changed) {
        if (changed.has('disabled')) {
            __classPrivateFieldGet(this, _FsChoiceButtonGroup_internals, "f").ariaDisabled = this.disabled.toString();
            for (const element of this._defaultSlot.assignedElements()) {
                element.disabled = this.disabled;
            }
        }
        if (changed.has('compact')) {
            for (const element of this._defaultSlot.assignedElements()) {
                element.compact = this.compact;
            }
        }
    }
}
_FsChoiceButtonGroup_internals = new WeakMap(), _FsChoiceButtonGroup_slotChangeController = new WeakMap(), _FsChoiceButtonGroup_instances = new WeakSet(), _FsChoiceButtonGroup_onSlotChange = function _FsChoiceButtonGroup_onSlotChange(event) {
    __classPrivateFieldGet(this, _FsChoiceButtonGroup_slotChangeController, "f")?.abort();
    __classPrivateFieldSet(this, _FsChoiceButtonGroup_slotChangeController, new AbortController(), "f");
    const options = { signal: __classPrivateFieldGet(this, _FsChoiceButtonGroup_slotChangeController, "f").signal };
    for (const element of event.target.assignedElements()) {
        element.addEventListener('invalid', (event) => {
            this.errorMessage = event.target.validationMessage;
        }, options);
        element.addEventListener('change', () => {
            this.errorMessage = '';
        }, options);
    }
};
FsChoiceButtonGroup.canonicalName = groupCanonicalName;
FsChoiceButtonGroup.styles = [
    css `:host {
      display: block;
    }
    :host([direction="column"]) ::slotted(*) {
      width: unset;
    }`,
    fieldLabelStyles,
    formFieldStyles,
    choiceButtonStyles
];
/** Enable group validation behavior */
FsChoiceButtonGroup.formControlValidationGroup = true;
__decorate([
    property({ type: Boolean, reflect: true })
], FsChoiceButtonGroup.prototype, "compact", void 0);
__decorate([
    property()
], FsChoiceButtonGroup.prototype, "direction", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsChoiceButtonGroup.prototype, "disabled", void 0);
__decorate([
    property({ attribute: 'error-message' })
], FsChoiceButtonGroup.prototype, "errorMessage", void 0);
__decorate([
    property({ type: String, attribute: 'field-label' })
], FsChoiceButtonGroup.prototype, "fieldLabel", void 0);
__decorate([
    property({ attribute: 'helper-text' })
], FsChoiceButtonGroup.prototype, "helperText", void 0);
__decorate([
    property({ type: Number, attribute: 'items-per-row' })
], FsChoiceButtonGroup.prototype, "itemsPerRow", void 0);
__decorate([
    query('#default')
], FsChoiceButtonGroup.prototype, "_defaultSlot", void 0);
__decorate([
    state()
], FsChoiceButtonGroup.prototype, "validationMessage", void 0);
//# sourceMappingURL=ChoiceButtonGroup.js.map